import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import * as XLSX from 'xlsx';
import supplierApi, { supplierError } from '../../services/supplierApi';
import { EMPTY_SUPPLIER, type SupplierConfig, type ValidatedRow } from '../../types/suppliers';
import { ROW_STATUS, parseSupplierTable, resultSheet, type ParsedRow } from '../../utils/supplierImport';

/**
 * Petty Cash > Suppliers > Upload from Excel: download the template (drop-down lists from SAP), upload the
 * filled file (read here in the browser), see every row checked against SAP, then create the ready rows one
 * by one. Rows with a problem are skipped; the result file lists each row with its new code or the reason.
 */

type Phase = 'idle' | 'reading' | 'checked' | 'creating' | 'done';
type RunState = { status: 'Creating' | 'Created' | 'Failed'; cardCode?: string; detail?: string };

const creatable = (r: ValidatedRow) => r.status === 'Ready' || r.status === 'Warning';

function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}

const SupplierUpload: React.FC<{ companyId: number; config: SupplierConfig; onCreated?: () => void }> = ({ companyId, config, onCreated }) => {
  const [phase, setPhase] = React.useState<Phase>('idle');
  const [fileName, setFileName] = React.useState('');
  const [parsed, setParsed] = React.useState<ParsedRow[]>([]);
  const [unknownHeaders, setUnknownHeaders] = React.useState<string[]>([]);
  const [results, setResults] = React.useState<ValidatedRow[]>([]);
  const [run, setRun] = React.useState<Record<number, RunState>>({});
  const [error, setError] = React.useState('');
  const [templateBusy, setTemplateBusy] = React.useState(false);
  const stop = React.useRef(false);

  const downloadTemplate = async () => {
    setTemplateBusy(true);
    setError('');
    try {
      const { blob, fileName: name } = await supplierApi.downloadTemplate(companyId);
      saveBlob(blob, name);
    } catch (e) {
      setError(supplierError(e, 'The template could not be built'));
    } finally {
      setTemplateBusy(false);
    }
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setFileName(file.name);
    setError('');
    setRun({});
    setResults([]);
    setPhase('reading');
    try {
      // raw: numbers stay numbers (a 15-digit VAT number in full), CSV text stays as typed.
      const book = XLSX.read(await file.arrayBuffer(), { type: 'array', raw: true });
      const sheetName = book.SheetNames.find((n) => n.trim().toLowerCase() === 'suppliers') || book.SheetNames[0];
      const ws = book.Sheets[sheetName];
      // Blank rows kept, so each row's place is its Excel row counted from where the sheet's range starts.
      const table = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '', blankrows: true }) as unknown[][];
      const firstRow = ws?.['!ref'] ? XLSX.utils.decode_range(ws['!ref']).s.r + 1 : 1;
      const sheet = parseSupplierTable(table, firstRow);
      if (!sheet.rows.length) throw new Error('No suppliers found. Keep the column names in the first row (see the template).');
      if (sheet.rows.length > config.maxRows) throw new Error(`The file has ${sheet.rows.length} suppliers; upload at most ${config.maxRows} at a time.`);
      setParsed(sheet.rows);
      setUnknownHeaders(sheet.unknownHeaders);
      const checked = await supplierApi.validate(companyId, sheet.rows.map((r) => ({ ...EMPTY_SUPPLIER, ...r.values, rowNum: r.rowNum })));
      setResults(checked.rows);
      setPhase('checked');
    } catch (e) {
      setParsed([]);
      setPhase('idle');
      setError(`Could not read ${file.name}: ${supplierError(e, 'unknown error')}`);
    }
  };

  const ready = results.filter(creatable);
  const counts = {
    ready: results.filter((r) => r.status === 'Ready').length,
    warning: results.filter((r) => r.status === 'Warning').length,
    sap: results.filter((r) => r.status === 'AlreadyInSap').length,
    file: results.filter((r) => r.status === 'DuplicateInFile').length,
    error: results.filter((r) => r.status === 'Error').length,
  };
  const done = Object.values(run).filter((r) => r.status !== 'Creating').length;

  const createAll = async () => {
    stop.current = false;
    setPhase('creating');
    for (const row of ready) {
      if (stop.current) break;
      if (run[row.rowNum]?.status === 'Created') continue;
      setRun((s) => ({ ...s, [row.rowNum]: { status: 'Creating' } }));
      const values = parsed.find((p) => p.rowNum === row.rowNum)?.values || {};
      try {
        const created = await supplierApi.create(companyId, { ...EMPTY_SUPPLIER, ...values }, 'Excel', { fileName, rowNum: row.rowNum });
        setRun((s) => ({ ...s, [row.rowNum]: { status: 'Created', cardCode: created.cardCode, detail: created.warnings.join('; ') } }));
      } catch (e) {
        setRun((s) => ({ ...s, [row.rowNum]: { status: 'Failed', detail: supplierError(e, 'SAP refused the supplier') } }));
      }
    }
    setPhase('done');
    onCreated?.();
  };

  const downloadResult = () => {
    const rows = results.map((r) => {
      const state = run[r.rowNum];
      return {
        rowNum: r.rowNum,
        values: parsed.find((p) => p.rowNum === r.rowNum)?.values || {},
        status: state ? ROW_STATUS[state.status].label : ROW_STATUS[r.status].label,
        cardCode: state?.cardCode || r.existing?.cardCode || '',
        detail: state?.detail || r.messages.join('; '),
      };
    });
    const sheet = XLSX.utils.aoa_to_sheet(resultSheet(rows));
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Result');
    XLSX.writeFile(book, `${fileName.replace(/\.[^.]+$/, '') || 'Suppliers'}_result.xlsx`);
  };

  const step = (n: number, label: string, state: 'done' | 'current' | 'todo') => (
    <Stack component="li" direction="row" spacing={1} alignItems="center" sx={{ fontWeight: state === 'current' ? 600 : 400, color: state === 'todo' ? 'text.secondary' : 'text.primary' }}>
      <Box
        aria-hidden="true"
        sx={{
          width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12,
          bgcolor: state === 'done' ? 'success.dark' : state === 'current' ? '#fdf3cf' : 'transparent',
          color: state === 'done' ? '#fff' : 'text.primary',
          border: state === 'done' ? 'none' : `${state === 'current' ? 2 : 1}px solid`,
          borderColor: state === 'current' ? '#E8B640' : 'grey.500',
        }}
      >
        {n}
      </Box>
      <Typography variant="body2" component="span" fontWeight="inherit">{label}</Typography>
    </Stack>
  );
  const stepState = (n: number): 'done' | 'current' | 'todo' => {
    const at = phase === 'idle' || phase === 'reading' ? 2 : phase === 'checked' ? 3 : 4;
    if (phase === 'done' && n === 4) return 'done';
    return n < at ? 'done' : n === at ? 'current' : 'todo';
  };

  return (
    <Stack spacing={2}>
      <Box component="ol" aria-label="Steps" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: '8px 28px' }}>
        {step(1, 'Download the template', stepState(1))}
        {step(2, 'Upload the filled file', stepState(2))}
        {step(3, 'Review the rows', stepState(3))}
        {step(4, 'Create in SAP', stepState(4))}
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography component="h2" variant="subtitle1" fontWeight={600}>{fileName || 'No file chosen yet'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {phase === 'reading' ? 'Reading the file and checking every row against SAP…'
                : results.length ? `${results.length} row${results.length === 1 ? '' : 's'} read in your browser and checked against SAP`
                  : `Fill the template (at most ${config.maxRows} suppliers), then upload it here.`}
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={templateBusy ? <CircularProgress size={16} /> : <DownloadIcon />} onClick={() => void downloadTemplate()} disabled={templateBusy}>
            Download template
          </Button>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={phase === 'reading' || phase === 'creating'}>
            {fileName ? 'Choose another file' : 'Choose file'}
            <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }} />
          </Button>
        </Stack>
        {phase === 'reading' ? <LinearProgress sx={{ mt: 2 }} aria-label="Checking the rows" /> : null}
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {unknownHeaders.length ? <Alert severity="info">Columns PRPO does not know were left out: {unknownHeaders.join(', ')}</Alert> : null}

      {results.length ? (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap aria-label="Row summary">
            <Chip size="small" color="success" label={`${counts.ready} ready`} />
            {counts.warning ? <Chip size="small" color="warning" label={`${counts.warning} ready with a warning`} /> : null}
            {counts.sap ? <Chip size="small" color="error" label={`${counts.sap} already in SAP`} /> : null}
            {counts.file ? <Chip size="small" color="warning" label={`${counts.file} duplicate${counts.file === 1 ? '' : 's'} in the file`} /> : null}
            {counts.error ? <Chip size="small" color="error" label={`${counts.error} error${counts.error === 1 ? '' : 's'}`} /> : null}
          </Stack>

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader aria-label="Rows of the file">
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>Supplier name</TableCell>
                  <TableCell>VAT number</TableCell>
                  <TableCell>CRN</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((r) => {
                  const state = run[r.rowNum];
                  const shown = state ? ROW_STATUS[state.status] : ROW_STATUS[r.status];
                  const detail = state
                    ? (state.status === 'Created' ? `Created as ${state.cardCode}${state.detail ? `. ${state.detail}` : ''}` : state.detail || '')
                    : r.messages.join('; ') || (r.status === 'Ready' ? `Gets the next vendor code ${config.numbering.manual ? '(PRPO works it out)' : 'from SAP'}` : '');
                  return (
                    <TableRow key={r.rowNum}>
                      <TableCell>{r.rowNum}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                      <TableCell>{r.vatShown || <Typography variant="body2" color="text.secondary" component="span">empty</Typography>}</TableCell>
                      <TableCell>{r.crn}</TableCell>
                      <TableCell><Chip size="small" color={shown.color} label={shown.label} /></TableCell>
                      <TableCell sx={{ maxWidth: 420 }}>{detail}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {phase === 'creating' ? (
            <Stack spacing={1}>
              <LinearProgress variant="determinate" value={ready.length ? (done / ready.length) * 100 : 0} aria-label="Creating suppliers" />
              <Typography variant="body2">{done} of {ready.length} done</Typography>
            </Stack>
          ) : null}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              Rows with a problem are skipped. Fix them in the file and upload it again; rows already created are then reported as already in SAP.
            </Typography>
            <Button variant="outlined" onClick={downloadResult} disabled={phase === 'creating'}>Download result file</Button>
            {phase === 'creating' ? (
              <Button variant="outlined" color="error" onClick={() => { stop.current = true; }}>Stop after this one</Button>
            ) : (
              <Button onClick={() => void createAll()} disabled={!ready.length || phase === 'done' || config.blockingFields.length > 0}>
                {`Create ${ready.length} supplier${ready.length === 1 ? '' : 's'}`}
              </Button>
            )}
          </Stack>
        </>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography component="h2" variant="subtitle2" gutterBottom>What the template holds</Typography>
        <Typography variant="body2">
          Columns: Supplier name, Arabic name, VAT registration number, CRN, Group, Currency, Payment terms, Country, City, District,
          Street, Building no., Postal code, Mobile, Phone, E-mail, Contact person.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Drop-down lists read from SAP: groups, currencies, payment terms, countries. Up to {config.maxRows} rows per file.
          {config.company.vatRule ? ` VAT numbers: ${config.company.vatRule}.` : ''}
        </Typography>
      </Paper>
    </Stack>
  );
};

export default SupplierUpload;
